import {
  isHelpAnnouncementLive,
  type HelpPlatform,
  type HelpRelease,
} from "@fitness-app/data-models/entities/HelpArticle";
import { compareDottedVersions, parseVersion } from "../users/compareClientAppVersion";

/**
 * Kto patrzy — aplikacja czy przeglądarka, a jeśli aplikacja, to jaka i w
 * jakiej wersji. Wersję podaje samo urządzenie (`NativeFingerprintService`),
 * więc decyzję „czy ta klientka ma już tę zmianę” podejmujemy tam, gdzie jest
 * ona pewna, zamiast zgadywać jej po `user_status` na serwerze.
 */
export interface HelpAnnouncementViewer {
  surface: "mobile" | "web";
  /** Tylko dla `surface: "mobile"` — wpis „Co nowego” rozróżnia wydania iOS i Androida. */
  platform?: "ios" | "android" | null;
  /** Format `1.1.38 (170) - 1.38.2`, czyli to, co zwraca fingerprint urządzenia. */
  appVersion?: string | null;
}

export interface HelpAnnouncementCandidate {
  id: string;
  slug: string;
  title: string;
  summary: string;
  platforms: string[];
  releases: unknown;
  announcedAt: string | null;
  /**
   * Opcjonalne, bo `help_announcements_for_me` sprawdza okno po stronie bazy i
   * nie oddaje tych pól klientce — kryteria ogłoszenia nie są jej sprawą.
   */
  announceUntil?: string | null;
  announceCtaText?: string | null;
  announcePushSentAt?: string | null;
}

const parseReleases = (releases: unknown): HelpRelease[] =>
  Array.isArray(releases) ? (releases as HelpRelease[]) : [];

const matchesSurface = (platforms: string[], viewer: HelpAnnouncementViewer): boolean => {
  const list = platforms as HelpPlatform[];

  if (viewer.surface === "web") {
    return list.includes("web");
  }

  // „Aplikacja” to wspólny wybór dla obu systemów; wpis o wydaniu tylko na
  // jednym z nich nosi konkretną platformę.
  return list.includes("mobile") || (!!viewer.platform && list.includes(viewer.platform));
};

/**
 * Czy urządzenie niesie już wydanie, o którym mówi wpis.
 *
 * Brak wersji, wersja nie do sparsowania albo wpis bez wydania dla tej
 * platformy = pokazujemy. Ogłoszenie jest informacją, nie dostępem do funkcji,
 * a zamilknięcie przy niepewnych danych kosztuje więcej niż popup u kogoś, kto
 * zmianę zobaczy dzień później. Odwrotnie jest, gdy wiemy na pewno: klientka ze
 * starszym buildem dostałaby popup o czymś, czego w swojej aplikacji nie
 * znajdzie — i to blokujemy.
 */
export const viewerHasRelease = (releases: unknown, viewer: HelpAnnouncementViewer): boolean => {
  if (viewer.surface === "web" || !viewer.platform) {
    // Przeglądarka jest zawsze w najnowszej wersji — wdrożenie dociera do
    // wszystkich naraz, więc nie ma czego porównywać.
    return true;
  }

  const release = parseReleases(releases).find((item) => item.platform === viewer.platform);

  if (!release) {
    return true;
  }

  const required = parseVersion(release.version);
  const current = viewer.appVersion ? parseVersion(viewer.appVersion) : null;

  if (!required || !current) {
    return true;
  }

  const native = compareDottedVersions(current.nativeVersion, required.nativeVersion);

  if (native !== 0) {
    return native > 0;
  }

  // Ten sam build natywny: o tym, czy zmiana już doszła, decyduje paczka JS
  // (wydanie OTA).
  return compareDottedVersions(current.jsCodeVersion, required.jsCodeVersion) >= 0;
};

/**
 * Ta sama decyzja, ale podjęta na serwerze przy wysyłce pusha — na podstawie
 * urządzeń zapisanych w `user_status`. Aplikacja pyta o siebie samą, push musi
 * zgadnąć z ostatniego znanego stanu, więc wystarczy JEDNO urządzenie, które
 * zobaczyłoby popup: klientka z nowym telefonem i starym tabletem ma tę zmianę
 * u siebie.
 *
 * Sprawdzamy PLATFORMĘ wpisu razem z wersją. Sama wersja nie wystarcza: wpis
 * wydany tylko na iOS nie ma wydania androidowego do porównania, więc
 * `viewerHasRelease` przepuszcza Androida — a popup i tak odrzuci go po
 * platformie i tapnięcie pusha wyląduje na pustym ekranie głównym.
 */
export const anyDeviceCanSeeAnnouncement = (
  article: { platforms: string[]; releases: unknown },
  devices: { systemName?: string | null; os?: string | null; version?: string | null }[],
): boolean =>
  devices.some((device) => {
    const system = (device.systemName || device.os || "").toLowerCase();
    const platform = system.includes("ios") ? "ios" : system.includes("android") ? "android" : null;

    if (!platform) {
      return false;
    }

    const viewer: HelpAnnouncementViewer = {
      surface: "mobile",
      platform,
      appVersion: device.version ?? null,
    };

    return matchesSurface(article.platforms, viewer) && viewerHasRelease(article.releases, viewer);
  });

/**
 * Czy któreś urządzenie klientki jest na platformie, której dotyczy wpis -
 * niezależnie od wersji. Odróżnia „ma starszą wersję” (warto poprosić o
 * aktualizację) od „ma inny system” (żadna aktualizacja nie da jej tej zmiany,
 * np. wpis tylko o iOS u klientki z samym Androidem) - TRE-2259.
 */
export const anyDeviceOnAnnouncementPlatform = (
  article: { platforms: string[] },
  devices: { systemName?: string | null; os?: string | null }[],
): boolean =>
  devices.some((device) => {
    const system = (device.systemName || device.os || "").toLowerCase();
    const platform = system.includes("ios") ? "ios" : system.includes("android") ? "android" : null;

    return !!platform && matchesSurface(article.platforms, { surface: "mobile", platform });
  });

/** Czy o wpisie w ogóle ma iść push — czyli czy dotyczy aplikacji. */
export const isAnnouncementPushable = (platforms: string[]): boolean =>
  matchesSurface(platforms, { surface: "mobile", platform: "ios" }) ||
  matchesSurface(platforms, { surface: "mobile", platform: "android" });

export const isHelpAnnouncementVisibleTo = (
  article: HelpAnnouncementCandidate,
  viewer: HelpAnnouncementViewer,
  now = new Date(),
): boolean =>
  isHelpAnnouncementLive(article, now) &&
  matchesSurface(article.platforms, viewer) &&
  viewerHasRelease(article.releases, viewer);

/**
 * Pierwszy wpis do pokazania: najnowszy ogłoszony, którego klientka jeszcze nie
 * widziała i który pasuje do jej urządzenia. Jeden popup na wejście — reszta
 * poczeka, bo dwa modale pod rząd czyta się tak samo źle jak dwa naraz.
 */
export const pickHelpAnnouncement = (
  articles: HelpAnnouncementCandidate[],
  seenArticleIds: Set<string>,
  viewer: HelpAnnouncementViewer,
  now = new Date(),
): HelpAnnouncementCandidate | null =>
  articles.find((article) => !seenArticleIds.has(article.id) && isHelpAnnouncementVisibleTo(article, viewer, now)) ??
  null;
